import { z } from "zod"
import { WORK_ENVIRONMENT } from "./enums"
import {
	age,
	bool,
	city,
	degreeName,
	description,
	disability,
	email,
	fieldOfWork,
	gender,
	hours,
	jobType,
	level,
	maritalStatus,
	name,
	neighborhood,
	organizationSector,
	organizationSize,
	phone,
	salary,
	timings,
	title,
	username,
	weeklyRestDays,
} from "./_"

export const MAX_FILE_SIZE = 12 * 1024 * 1024
export const ACCEPTED_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/gif",
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]

export const ACCEPTED_DOCUMENT_TYPES = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]

export const requiredImage = z.any().superRefine((file, ctx) => {
	if (!(file instanceof File)) {
		ctx.addIssue({ code: "custom", message: "@q:error_required_file" })
		return
	}
	if (file.size > MAX_FILE_SIZE) {
		ctx.addIssue({
			code: "custom",
			message: "@q:error_max_file_size",
		})
	}
	if (!ACCEPTED_TYPES.includes(file.type)) {
		ctx.addIssue({
			code: "custom",
			message: "@q:error_accepted_types",
		})
	}
})

export const optionalImage = z
	.any()
	.optional()
	.superRefine((file, ctx) => {
		if (file == null) return
		if (!(file instanceof File)) {
			ctx.addIssue({
				code: "custom",
				message: "@q:error_required_file",
			})
			return
		}
		if (file.size > MAX_FILE_SIZE) {
			ctx.addIssue({
				code: "custom",
				message: "@q:error_max_file_size",
			})
		}
		if (!ACCEPTED_TYPES.includes(file.type)) {
			ctx.addIssue({
				code: "custom",
				message: "@q:error_accepted_types",
			})
		}
	})

export const organizationEditSchema = z.object({
	name: name(),
	email: email(),
	phone: phone(),
	city: city(),
	neighborhood: neighborhood(),
	size: organizationSize(),
	sector: organizationSector(),
	certified: bool(),
	logo: optionalImage,
})

export const employeeSettingsSchema = z.object({
	name: name(),
	phone: phone(),
	username: username(),
	email: email(),
	city: city(),
	neighborhood: neighborhood(),
	age: age(),
	gender: gender(false),
	maritalStatus: maritalStatus(),
	isSaudi: z.boolean(),
	hasCarLicense: z.boolean(),
	jobType: jobType(),
	englishLevel: level(),
	arabicLevel: level(),
	computerSkills: level(),
	workEnvironment: z.array(z.enum(WORK_ENVIRONMENT)),
})

export type EmployeeSettingsFormData = z.infer<typeof employeeSettingsSchema>

export const requiredDocument = z.any().superRefine((file, ctx) => {
	if (!(file instanceof File)) {
		ctx.addIssue({ code: "custom", message: "@q:error_required_file" })
		return
	}
	if (file.size > MAX_FILE_SIZE) {
		ctx.addIssue({
			code: "custom",
			message: "@q:error_max_file_size",
		})
	}
	if (!ACCEPTED_DOCUMENT_TYPES.includes(file.type)) {
		ctx.addIssue({
			code: "custom",
			message: "@q:error_accepted_types",
		})
	}
})

export const optionalDocument = z
	.any()
	.optional()
	.superRefine((file, ctx) => {
		if (file == null) return
		if (!(file instanceof File)) {
			ctx.addIssue({
				code: "custom",
				message: "@q:error_required_file",
			})
			return
		}
		if (file.size > MAX_FILE_SIZE) {
			ctx.addIssue({
				code: "custom",
				message: "@q:error_max_file_size",
			})
		}
		if (!ACCEPTED_DOCUMENT_TYPES.includes(file.type)) {
			ctx.addIssue({
				code: "custom",
				message: "@q:error_accepted_types",
			})
		}
	})

export const employeeAttachmentsSchema = z
	.object({
		cv: optionalImage,
		disabilityScene: optionalImage,
	})
	.refine((data) => data.cv || data.disabilityScene, {
		message: "@q:error_required_file",
		path: ["cv"],
	})

export const jobEditSchema = z.object({
	jobId: z.string().min(1),
	title: title(),
	description: description(),
	timings: timings(),
	city: city(),
	neighborhood: neighborhood(),
	fieldOfWork: fieldOfWork(),
	gender: gender(true),
	requirements: z.string().min(1),
	extra: z.string().optional().default(""),
	degree: degreeName().optional(),
	specialization: z.string().optional(),
	suitableDisabilities: disability().array().default([]),
	notSuitableDisabilities: disability().array().default([]),
	hours: hours().optional(),
	weeklyRestDays: weeklyRestDays().optional(),
	medicalInsurance: z.string().optional(),
	capacity: z.number().min(1).optional(),
	salary: z.number().min(0).optional(),
	housingAllowance: z.number().min(0).optional(),
	anotherAllowance: z.number().min(0).optional(),
	rawSalary: salary().optional(),
	type: jobType().optional(),
})

export type JobEditFormValues = z.infer<typeof jobEditSchema>

export type EmployeeAttachmentsFormData = z.infer<
	typeof employeeAttachmentsSchema
>
